import { CollectionViewer } from '@angular/cdk/collections';
import { DataSource } from '@angular/cdk/table';
import { MatTableDataSource } from '@angular/material/table';
import { Constants } from '@app/shared/constants';
import { Entity } from '@app/shared/models/models.interface';
import { Page, SortParam } from '@app/shared/models/page.interface';
import { StateService } from '@app/shared/services/state-service.interface';
import { ListState } from '@app/shared/store/list-store';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';

export class TableDataSource<T extends Entity = any, S = any> implements DataSource<T> {
  protected subject = new BehaviorSubject<T[]>([]);
  public useStoredData = false;
  protected state: ListState<T> = null;
  protected id: string = null;

  /**
   * Constructor
   *
   * @param service the service to obtain the data
   * @param id optional id (only necessary when the service supports more that
   *         one list state)
   */
  constructor(protected service: StateService<T, S>, id?: string) {
    this.state = service.getDataSourceState(id);
    this.id = id;
  }

  /**
   * Connects a collection viewer (such as a data-table) to this data source. Note that
   * the stream provided will be accessed during change detection and should not directly change
   * values that are bound in template views.
   * @param collectionViewer The component that exposes a view over the data provided by this
   *     data source.
   * @returns Observable that emits a new value when the data changes.
   */
  connect(collectionViewer: CollectionViewer): Observable<T[]> {
    return this.subject.asObservable();
  }

  /**
   * Disconnects a collection viewer (such as a data-table) from this data source. Can be used
   * to perform any clean-up or tear-down operations when a view is being destroyed.
   *
   * @param collectionViewer The component that exposes a view over the data provided by this
   *     data source.
   */
  disconnect(collectionViewer: CollectionViewer): void {}

  /** Be sure that this method is invoked */
  close() {
    this.subject.complete();
  }

  /**
   * Get the elements
   */
  getElements(): T[] {
    return this.state.elements.content;
  }

  /**
   * Get the loading state
   */
  loading$(): Observable<boolean> {
    return of(this.state.loading);
  }

  /**
   * Get the number of elements
   */
  total$(): Observable<number> {
    if (this.useStoredData) {
      if (!this.state.elements.content) {
        this.state.elements.content = [];
      }
      return of(this.state.elements.content.length);
    } else {
      return of(this.state.elements.totalElements);
    }
  }

  /**
   * Remove data
   */
  removeData() {
    if (this.state.elements && this.service) {
      this.state.elements.content = [];
    }
  }

  /**
   * Get all the data applying no filters and a default start/page size
   *
   * @param field sort field
   */
  getAllSimple(field: string) {
    const sort: SortParam = {
      field,
      direction: Constants.ASC_DIR
    };
    this.getAll(Constants.DATATABLE_INITAL_PAGE, Constants.DATATABLE_PAGE_SIZE, [sort], '');
  }

  /**
   * Sometimes is necessary to switch to an in memory datasource...
   */
  protected getAllInMemory(page: string, size: string, sort: SortParam[], filter: string) {
    let content = this.state.elements.content;
    if (content) {
      // TODO: filter: change "," and ":" by special chars to avoid conflicts with values
      if (filter !== '') {
        // The filter format is like this:
        //  name:a,surname:a,username:a
        const filters = filter.split(',');
        const filteredContent = [];
        let found = false;
        content.forEach(value => {
          found = false;
          filters.forEach(f => {
            const tokens = f.split(':');
            const key = tokens[0];
            const val = tokens[1].toLowerCase();

            if (value[key] && value[key].toLowerCase().includes(val)) {
              found = true;
            }
          });
          if (found) {
            filteredContent.push(value);
          }
        });
        content = filteredContent;
      }

      /** TODO: improve the multiple column search */
      // Sort
      if (sort[0].field != null && sort[0].field !== '') {
        content.sort((a, b) => this.defaultSort(a, b, sort));
      }

      const pg = parseInt(page, 10);
      const sz = parseInt(size, 10);
      const start = pg * sz;
      this.subject.next(content.slice(start, start + sz));
    } else {
      this.subject.next([]);
    }
  }

  protected defaultSort(a: T, b: T, sort: SortParam[]) {
    let val1: string = a[sort[0].field];
    let val2: string = b[sort[0].field];
    if (sort[0].direction === Constants.ASC_DIR) {
      return this.localeCompare(val1, val2);
    } else {
      return this.localeCompare(val2, val1);
    }
  }

  protected localeCompare(s1: string, s2: string) {
    if (s1 != null) {
      return s1.localeCompare(s2);
    } else if (s2 != null) {
      return 1;
    }
    return 0;
  }

  /**
   * Get all the items. This method initially uses the service to get the data,
   * but is prepared to use the data provided by the state.
   *
   * @param page page (zero based)
   * @param size size
   * @param sort an array of SortParams
   * @param filter filters
   */
  getAll(page: string, size: string, sort: SortParam[], filter: string) {
    if (this.useStoredData) {
      this.getAllInMemory(page, size, sort, filter);
    } else if (this.service) {
      this.state.loading = true;
      this.service
        .getAll(page, size, sort, filter)
        .pipe(
          catchError(() => of([])), // TODO We need to handle the error here
          finalize(() => {
            this.state.loading = false;
            this.service.setDataSourceState(this.state, this.id);
          })
        )
        .subscribe((data: Page<T>) => {
          this.state.elements = data;
          this.subject.next(data.content);
        });
    }
  }
}

export class DataTableMatDataSource<T extends Entity, S> extends MatTableDataSource<T> {
  protected subject = new BehaviorSubject<T[]>([]);
  public useStoredData = false;
  protected state: ListState<T> = null;
  protected id: string = null;

  /**
   * Constructor
   *
   * @param service the service to obtain the data
   * @param id optional id (only necessary when the service supports more that
   *         one list state)
   */
  constructor(data: T[]) {
    super(data);
  }

  /**
   * Get the elements
   */
  // getElements(): T[] {
  //   return this.state.elements.content;
  // }

  /**
   * Get the loading state
   */
  loading$(): Observable<boolean> {
    return of(false);
  }

  /**
   * Get the number of elements
   */
  total$(): Observable<number> {
    return of(this.data.length);
  }

  /** Be sure that this method is invoked */
  close() {
    this.subject.complete();
  }

  /**
   * Remove data
   */
  // removeData() {
  //   if (this.state.elements && this.service) {
  //     this.state.elements.content = [];
  //   }
  // }

  /**
   * Get all the items. This method initially uses the service to get the data,
   * but is prepared to use the data provided by the state.
   *
   * @param page page (zero based)
   * @param size size
   * @param sort an array of SortParams
   * @param filter filters
   */
  getAll(page: string, size: string, sort: SortParam[], filter: string) {
    return this.data;
  }
}
